import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { DateRangePicker, DateRangePickerInputProps } from '@creditornot/cb-date-picker';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { MenuItemGroup } from '@creditornot/cb-menu';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { EARLIEST_SUPPORTED_DATETIME_FROM_NOW_IN_DAYS } from './constants';

export type IsoDateRange = {
  from: string; // ISO date, eg. 2021-01-15
  to: string; // ISO date, eg. 2021-01-15
};

export type Period = 'today' | 'yesterday' | 'this-week' | `${string}--${string}`;

export type FilterDateRangePickerProps = Omit<DateRangePickerInputProps, 'value' | 'onChange'> & {
  value?: IsoDateRange;
  onDateRangeChange: (period: Period) => void;
  withDelayNotice?: boolean;
  timezone: string;
};

type DateRange = {
  from: Date | undefined;
  to?: Date | undefined;
};

export const isoDateToJsDate = (isoDate: string, timezone: string) => {
  // Parse isoDate manually instead of letting the Date constructor do it, because it considers ISO dates (usually) as
  // UTC while the Date object itself is always in the browser's local timezone. Here, we consider isoDate to be in the
  // browser's local timezone, because DateRangePickerInput works in local timezone.
  //
  // Also, support for ISO strings is not standardized.
  //
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date#parameters
  const [yearString, monthString, dayString] = isoDate.split('-');
  const year = parseInt(yearString, 10);
  const monthIndex = parseInt(monthString, 10) - 1;
  const day = parseInt(dayString, 10);
  return DateTime.fromObject({ year, month: monthIndex + 1, day }, { zone: timezone }).toJSDate();
};

const isoDateRangeToJsDateRange = (value: IsoDateRange, timezone: string) => ({
  from: isoDateToJsDate(value.from, timezone),
  to: isoDateToJsDate(value.to, timezone),
});

export const jsDateToIsoDate = (jsDate: Date, timezone: string): string => {
  return DateTime.fromJSDate(jsDate).setZone(timezone).toISODate();
};

const FilterDateRangePickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 270px;
  padding: 0 10px;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;

  /*** scrollbar customization ***/

  /* Firefox */
  scrollbar-width: thin;
  /* ignore-color */
  scrollbar-color: ${colors.pepper32};

  /* Chrome/Edge/Safari */
  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${({ theme }) => (theme.cbThemeMode === 'light' ? colors.pepper4 : colors.pepper100)};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${cssThemedValue({ light: colors.pepper32, dark: colors.salt36 })};
    border-radius: 20px;
    border: 3px solid ${({ theme }) => theme.cbSemanticColors.surfaceMain};
  }
`;

const RowDivider = styled.div`
  margin-block-start: 16px;
  margin-block-end: 16px;
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  width: 100%;
`;

const DataDelayAlert = styled(Alert)`
  margin-block-start: 12px;
`;

const FilterDateRangePicker = ({ value, onDateRangeChange, withDelayNotice, timezone }: FilterDateRangePickerProps) => {
  const [now] = useState(Date.now());

  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    value ? isoDateRangeToJsDateRange(value, timezone) : undefined,
  );

  useEffect(() => {
    // Two way binding when the value changes.
    setDateRange(value ? isoDateRangeToJsDateRange(value, timezone) : undefined);
  }, [timezone, value]);

  const disabledDays = useMemo(
    () => ({
      before: DateTime.fromMillis(now).minus({ days: EARLIEST_SUPPORTED_DATETIME_FROM_NOW_IN_DAYS }).toJSDate(),
      after: new Date(now),
    }),
    [now],
  );

  const updateDateRange = ({ from, to }: DateRange) => {
    setDateRange({ from, to });
    if (!from) {
      if (!to) {
        throw new Error('Both `from` and `to` from date range are missing!');
      }
      onDateRangeChange(`${jsDateToIsoDate(to, timezone)}--${jsDateToIsoDate(to, timezone)}`);
    } else if (!to) {
      onDateRangeChange(`${jsDateToIsoDate(from, timezone)}--${jsDateToIsoDate(from, timezone)}`);
    } else {
      onDateRangeChange(`${jsDateToIsoDate(from, timezone)}--${jsDateToIsoDate(to, timezone)}`);
    }
  };

  const shouldShowDataDelayNotice =
    withDelayNotice &&
    dateRange &&
    dateRange.from &&
    dateRange.to &&
    DateTime.now().startOf('day') <= DateTime.fromJSDate(dateRange.to);

  return (
    <FilterDateRangePickerContainer>
      <DateRangePicker
        disabledDays={disabledDays}
        value={value ? isoDateRangeToJsDateRange(value, timezone) : undefined}
        onChange={updateDateRange}
        timezone={timezone}
      />

      {shouldShowDataDelayNotice && (
        <DataDelayAlert variant="error">
          <FormattedMessage id="purchases.labels.data-delay-purchase-history" />
        </DataDelayAlert>
      )}
      <RowDivider />
      <MenuItemGroup>
        <Button stretch variant="lightBlue" size="small" onClick={() => onDateRangeChange('today')}>
          <FormattedMessage id="purchases.labels.today" />
        </Button>
        <Button stretch variant="lightBlue" size="small" onClick={() => onDateRangeChange('yesterday')}>
          <FormattedMessage id="purchases.labels.yesterday" />
        </Button>
        <Button stretch variant="lightBlue" size="small" onClick={() => onDateRangeChange('this-week')}>
          <FormattedMessage id="purchases.labels.this-week" />
        </Button>
      </MenuItemGroup>
    </FilterDateRangePickerContainer>
  );
};

export default FilterDateRangePicker;
